/** Scope 3's Suppliers' share tab: what suppliers' own answers account for (ADR-0040, ADR-0167).
 *
 * One row per request this workspace sent a supplier. The tiles are the server's: the roll-up
 * (`GET /supplier-exchange/scope3-rollup`) sums the answers that were accepted into the inventory,
 * and a supplier's share is its own accepted figures over the window's Scope 3, both summed on the
 * server. **Additional information beside a filing and never added to a stated total** - whether a
 * regime states Scope 3 at all is regime data and rule 2's - which is what the tile's info control
 * says. A supplier that has not answered has no share, and says so with a dash: *nothing accepted*
 * and *a share of zero* are different answers.
 *
 * A supplier is another company's own workspace (ADR-0040), so this workspace holds no name for
 * one. The cell says `Supplier 1`, `Supplier 2` in the order they were first asked, never a
 * fragment of an id (G-P7-12; a name is ADR-0221, proposed), and links to the supplier's page.
 * Asking a supplier for data is the Vendors place's act, and the one button here goes there.
 */

import { Truck } from "@phosphor-icons/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { useScope3Rollup } from "../api/scope3";
import { useExchange, useSupplierScope3Share } from "../api/suppliers";
import type { Exchange } from "../api/suppliers";
import { ReadFailed } from "../components/Loaded";
import type { FilterState, GridColumn } from "../components/ui";
import { EMPTY_FILTERS, EmptyState, FilterLine, GridTable, TablePage, Tile, TileStrip } from "../components/ui";
import { shortDate, share, tonnes } from "../lib/format";
import { mayEnterData } from "../shell/routeTable";
import { DASH } from "./scopeTable/context";
import type { Scope3Shared } from "./scopeTable/context";
import { keepSearch } from "./search";
import { knownSuppliers, stateLabel, supplierName, supplierNumbers } from "./supplierLabels";
import own from "./scopeTable/scopeTable.module.css";

/** A supplier's share of the window's Scope 3, from the server's two sums. */
function ShareCell({ row, shared }: { row: Exchange; shared: Scope3Shared }) {
  const accepted = row.state === "accepted";
  const read = useSupplierScope3Share(
    accepted ? shared.period : undefined,
    shared.screen.method,
    row.supplier_org_id,
    row.id,
  );
  if (!accepted) return <span>—</span>;
  if (read.isPending) return <span aria-busy="true">…</span>;
  // Said in a word, not a dash: a dash is "this supplier has no share", which is a different answer.
  if (read.error || !read.data) return <span title="This supplier's share could not be read.">Not read</span>;
  const part = read.data.supplier_figures > 0 ? share(read.data.supplier_kgco2e, read.data.scope3_kgco2e) : null;
  return <span data-testid={`supplier-share-${row.id}`}>{part ?? "—"}</span>;
}

export function Scope3Suppliers(shared: Scope3Shared) {
  const { screen, period, role } = shared;
  const navigate = useNavigate();
  const exchange = useExchange();
  const rollup = useScope3Rollup(period, screen.method);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);

  const mine = (exchange.data ?? []).filter((row) => row.i_am === "buyer");
  const numbers = supplierNumbers(exchange.data ?? []);
  const supplierCell = (id: string) => supplierName(id, numbers);
  const state = filters.values["state"]?.[0];
  const needle = filters.search.trim().toLowerCase();
  const rows = mine.filter(
    (row) =>
      (state === undefined || row.state === state) &&
      (needle === "" ||
        `${supplierCell(row.supplier_org_id)} ${stateLabel(row.state)} ${row.scope3_category ?? ""}`
          .toLowerCase()
          .includes(needle)),
  );
  const states = [...new Set(mine.map((row) => row.state))];
  const dash = exchange.isPending || Boolean(exchange.error);

  const columns: GridColumn<Exchange>[] = [
    {
      id: "supplier",
      header: "Supplier",
      rowHeader: true,
      cell: (row) => (
        <Link
          to="/suppliers/$supplierId"
          params={{ supplierId: row.supplier_org_id }}
          search={keepSearch}
        >
          {supplierCell(row.supplier_org_id)}
        </Link>
      ),
    },
    {
      id: "category",
      header: "Category",
      numeric: true,
      cell: (row) => (row.scope3_category ? String(row.scope3_category) : "—"),
    },
    { id: "state", header: "Status", cell: (row) => stateLabel(row.state) },
    { id: "due", header: "Due", cell: (row) => (row.due_on ? shortDate(row.due_on) : "—") },
    { id: "share", header: "Share of Scope 3", numeric: true, cell: (row) => <ShareCell row={row} shared={shared} /> },
  ];

  return (
    <TablePage
      icon={Truck}
      title="Scope 3"
      actions={
        mayEnterData(role) ? (
          <Link
            to="/vendors"
            search={(prev) => ({ ...keepSearch(prev), tab: "requests" })}
            className={own.headLink}
            data-testid="scope3-request-data"
          >
            Request data
          </Link>
        ) : undefined
      }
      tiles={
        // The roll-up is a read of its own, and the table's failure does not cover it: a dash for
        // "supplier-attributed" with no sentence read as nothing having been attributed (G-P9-11).
        <TileStrip
          label="Suppliers' share"
          reads={[{ of: "The supplier-attributed total", read: rollup }]}
        >
          <Tile label="Suppliers asked" value={dash ? DASH : String(knownSuppliers(mine).length)} read={exchange} />
          <Tile
            label="Answers accepted"
            value={dash ? DASH : String(mine.filter((row) => row.state === "accepted").length)}
            read={exchange}
          />
          <Tile
            label="Waiting for an answer"
            value={dash ? DASH : String(mine.filter((row) => row.state === "open").length)}
            read={exchange}
          />
          <Tile
            label="Supplier-attributed"
            value={rollup.data ? tonnes(rollup.data.total_kgco2e) : DASH}
            unit={rollup.data ? "tCO₂e" : undefined}
            infoKey="scope3.suppliers"
            read={rollup}
            testId="tile-supplier-attributed"
          />
        </TileStrip>
      }
      filters={
        exchange.error ? undefined : (
          <FilterLine
            filters={[
              {
                id: "state",
                label: "Status",
                mode: "single",
                allLabel: "All statuses",
                options: states.map((value) => ({ value, label: stateLabel(value) })),
              },
            ]}
            state={filters}
            onChange={setFilters}
            searchLabel="Search this table"
            resultCount={exchange.isPending ? undefined : { shown: rows.length, total: mine.length, noun: "requests" }}
          />
        )
      }
      table={
        exchange.error ? (
          <ReadFailed of="The supplier exchange" error={exchange.error} onRetry={() => void exchange.refetch()} />
        ) : (
          <GridTable
            caption="Suppliers asked for Scope 3 data, and their share"
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
            rowLabel={(row) => supplierCell(row.supplier_org_id)}
            onRowOpen={(row) =>
              void navigate({
                to: "/suppliers/$supplierId",
                params: { supplierId: row.supplier_org_id },
                search: keepSearch,
              })
            }
            loading={exchange.isPending}
            empty={<EmptyState title="No supplier has been asked for data." compact />}
            testId="scope3-suppliers"
          />
        )
      }
    />
  );
}
